import React, { useState, useEffect } from 'react';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Building2, User, Mail, Phone, Users, Plane, Briefcase,
  Clock, CheckCircle, XCircle, MessageSquare, ChevronDown, ChevronUp,
  Filter, Search, RefreshCw, ExternalLink, AlertCircle, Bell, X, Plus, Settings
} from 'lucide-react';

const EarlyAccessRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [updating, setUpdating] = useState(null);

  // Notification settings state
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError, setNotifError] = useState('');
  const [notifSuccess, setNotifSuccess] = useState('');
  const [showNotifSettings, setShowNotifSettings] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    setNotifLoading(true);
    try {
      const response = await fetch(`${getApiBase()}/admin/early-access-notifications`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationEmails(Array.isArray(data.emails) ? data.emails : []);
      }
    } catch (err) {
      console.error('Failed to load notification settings:', err);
    } finally {
      setNotifLoading(false);
    }
  };

  const saveNotificationEmails = async (emails) => {
    setNotifSaving(true);
    setNotifError('');
    setNotifSuccess('');
    try {
      const response = await fetch(`${getApiBase()}/admin/early-access-notifications`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ emails })
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationEmails(Array.isArray(data.emails) ? data.emails : []);
        setNotifSuccess('Notification settings saved.');
        setTimeout(() => setNotifSuccess(''), 3000);
        return true;
      } else {
        const detail = await response.json().catch(() => ({}));
        setNotifError(detail.detail || 'Failed to save settings.');
        return false;
      }
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      setNotifError('Failed to save settings.');
      return false;
    } finally {
      setNotifSaving(false);
    }
  };

  const addNotificationEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (notificationEmails.includes(email)) {
      setNotifError('That email is already in the list.');
      return;
    }
    const ok = await saveNotificationEmails([...notificationEmails, email]);
    if (ok) setNewEmail('');
  };

  const removeNotificationEmail = async (email) => {
    await saveNotificationEmails(notificationEmails.filter((e) => e !== email));
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${getApiBase()}/admin/early-access-requests?status=${statusFilter}`
        : `${getApiBase()}/admin/early-access-requests`;

      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
      } else {
        let message = 'Failed to load requests';
        try {
          const err = await response.json();
          if (err?.detail) message = err.detail;
        } catch {
          // ignore
        }
        if (response.status === 401) {
          message = 'Session expired or not authenticated. Please sign in again.';
        } else if (response.status === 403) {
          message = "You don't have permission to view Early Access Requests. Ask a superadmin to grant the 'early_access' permission.";
        }
        setError(message);
      }
    } catch (err) {
      console.error('Failed to fetch early access requests:', err);
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId, newStatus, notes = null) => {
    setUpdating(requestId);
    try {
      const response = await fetch(`${getApiBase()}/admin/early-access-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus, notes })
      });

      if (response.ok) {
        // Refresh the list
        fetchRequests();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update request');
      }
    } catch (err) {
      console.error('Failed to update request:', err);
      alert('Failed to update request');
    } finally {
      setUpdating(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      contacted: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.company_name?.toLowerCase().includes(search) ||
      req.requester_name?.toLowerCase().includes(search) ||
      req.requester_email?.toLowerCase().includes(search) ||
      req.industry?.toLowerCase().includes(search)
    );
  });

  const statCounts = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    contacted: requests.filter(r => r.status === 'contacted').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Early Access Requests</h1>
          <p className="text-slate-600 mt-1">Manage beta program requests from potential customers</p>
        </div>
        <button
          onClick={() => setShowNotifSettings((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <Settings className="w-4 h-4" />
          Notification Settings
          {notificationEmails.length > 0 && (
            <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
              {notificationEmails.length}
            </span>
          )}
        </button>
      </div>

      {/* Notification Settings */}
      {showNotifSettings && (
        <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Staff Notifications</h2>
              <p className="text-sm text-slate-600">
                Email addresses here will be notified whenever a new early access request is submitted.
              </p>
            </div>
          </div>

          {notifError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{notifError}</span>
            </div>
          )}
          {notifSuccess && (
            <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-700">{notifSuccess}</span>
            </div>
          )}

          <div className="flex gap-2 mb-3">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addNotificationEmail();
                }
              }}
              placeholder="name@company.com"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={notifSaving || notifLoading}
            />
            <button
              onClick={addNotificationEmail}
              disabled={notifSaving || notifLoading || !newEmail.trim()}
              className="inline-flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>

          {notifLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : notificationEmails.length === 0 ? (
            <div className="text-sm text-slate-500">
              No notification emails configured. Add one above to receive alerts for new requests.
            </div>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {notificationEmails.map((email) => (
                <li
                  key={email}
                  className="inline-flex items-center gap-2 bg-slate-100 text-slate-800 rounded-full px-3 py-1 text-sm"
                >
                  <Mail className="w-3.5 h-3.5 text-slate-500" />
                  <span>{email}</span>
                  <button
                    onClick={() => removeNotificationEmail(email)}
                    disabled={notifSaving}
                    className="text-slate-500 hover:text-red-600 transition-colors disabled:opacity-50"
                    title="Remove"
                    aria-label={`Remove ${email}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-slate-900">{statCounts.total}</div>
          <div className="text-sm text-slate-500">Total Requests</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-yellow-600">{statCounts.pending}</div>
          <div className="text-sm text-slate-500">Pending</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-blue-600">{statCounts.contacted}</div>
          <div className="text-sm text-slate-500">Contacted</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-green-600">{statCounts.approved}</div>
          <div className="text-sm text-slate-500">Approved</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-2xl font-bold text-red-600">{statCounts.rejected}</div>
          <div className="text-sm text-slate-500">Rejected</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by company, name, email, or industry..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <button
              onClick={fetchRequests}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>No requests found</p>
        </div>
      ) : (
        /* Requests List */
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-lg border border-slate-200 overflow-hidden"
            >
              {/* Request Header */}
              <div
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{request.company_name}</h3>
                      {getStatusBadge(request.status)}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {request.requester_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {request.requester_email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {request.industry}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {request.employee_count} employees
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm text-slate-500">
                      {formatDate(request.created_at)}
                    </div>
                    {expandedRequest === request.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedRequest === request.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Company Details */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Company Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-slate-500">Industry:</span>{' '}
                          <span className="text-slate-900">{request.industry}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Employees:</span>{' '}
                          <span className="text-slate-900">{request.employee_count}</span>
                        </div>
                        {request.website && (
                          <div>
                            <span className="text-slate-500">Website:</span>{' '}
                            <a
                              href={request.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:underline inline-flex items-center gap-1"
                            >
                              {request.website}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                        {(request.headquarters_city || request.headquarters_country) && (
                          <div>
                            <span className="text-slate-500">Location:</span>{' '}
                            <span className="text-slate-900">
                              {[request.headquarters_city, request.headquarters_state, request.headquarters_country]
                                .filter(Boolean)
                                .join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact Details */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Contact Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-slate-500">Name:</span>{' '}
                          <span className="text-slate-900">{request.requester_name}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Title:</span>{' '}
                          <span className="text-slate-900">{request.requester_title}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Email:</span>{' '}
                          <a href={`mailto:${request.requester_email}`} className="text-purple-600 hover:underline">
                            {request.requester_email}
                          </a>
                        </div>
                        {request.requester_phone && (
                          <div>
                            <span className="text-slate-500">Phone:</span>{' '}
                            <span className="text-slate-900">{request.requester_phone}</span>
                          </div>
                        )}
                        {request.requester_department && (
                          <div>
                            <span className="text-slate-500">Department:</span>{' '}
                            <span className="text-slate-900">{request.requester_department}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Travel Details */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Travel Management
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-slate-500">Executive Assistants:</span>{' '}
                          <span className="text-slate-900">{request.num_executive_assistants}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Executives Supported:</span>{' '}
                          <span className="text-slate-900">{request.num_executives_supported}</span>
                        </div>
                        {request.monthly_trips_managed && (
                          <div>
                            <span className="text-slate-500">Monthly Trips:</span>{' '}
                            <span className="text-slate-900">{request.monthly_trips_managed}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Travel Agency */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Plane className="w-4 h-4" />
                        Travel Agency
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-slate-500">Uses Agency:</span>{' '}
                          <span className="text-slate-900">{request.has_travel_agency}</span>
                        </div>
                        {request.travel_agency_name && (
                          <div>
                            <span className="text-slate-500">Agency Name:</span>{' '}
                            <span className="text-slate-900">{request.travel_agency_name}</span>
                          </div>
                        )}
                        {request.agency_mandatory && (
                          <div>
                            <span className="text-slate-500">Policy:</span>{' '}
                            <span className="text-slate-900">{request.agency_mandatory}</span>
                          </div>
                        )}
                        {request.agency_exceptions && (
                          <div>
                            <span className="text-slate-500">Exceptions:</span>{' '}
                            <span className="text-slate-900">{request.agency_exceptions}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Current Tools */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Current Tools
                      </h4>
                      <div className="text-sm">
                        {request.current_tools && request.current_tools.length > 0 ? (
                          <ul className="list-disc list-inside text-slate-700 space-y-1">
                            {request.current_tools.map((tool, idx) => (
                              <li key={idx}>{tool}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-slate-500">None specified</span>
                        )}
                        {request.other_tools && (
                          <p className="mt-2 text-slate-600">Other: {request.other_tools}</p>
                        )}
                      </div>
                    </div>

                    {/* Challenges */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Challenges
                      </h4>
                      <div className="text-sm">
                        {request.biggest_challenges && request.biggest_challenges.length > 0 ? (
                          <ul className="list-disc list-inside text-slate-700 space-y-1">
                            {request.biggest_challenges.map((challenge, idx) => (
                              <li key={idx}>{challenge}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-slate-500">None specified</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="mt-6 grid md:grid-cols-2 gap-6">
                    {request.flight_tracking_method && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Flight Tracking Method</h4>
                        <p className="text-sm text-slate-700">{request.flight_tracking_method}</p>
                      </div>
                    )}
                    {request.itinerary_change_handling && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Itinerary Change Handling</h4>
                        <p className="text-sm text-slate-700">{request.itinerary_change_handling}</p>
                      </div>
                    )}
                  </div>

                  {/* Most Valuable Features */}
                  {request.most_valuable_features && request.most_valuable_features.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-slate-900 mb-2">Most Valuable Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {request.most_valuable_features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* How Heard About Us & Comments */}
                  <div className="mt-6 grid md:grid-cols-2 gap-6">
                    {request.how_heard_about_us && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">How They Heard About Us</h4>
                        <p className="text-sm text-slate-700">{request.how_heard_about_us}</p>
                      </div>
                    )}
                    {request.additional_comments && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Additional Comments</h4>
                        <p className="text-sm text-slate-700">{request.additional_comments}</p>
                      </div>
                    )}
                  </div>

                  {/* Internal Notes */}
                  {request.notes && (
                    <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h4 className="font-semibold text-yellow-800 mb-1">Internal Notes</h4>
                      <p className="text-sm text-yellow-700">{request.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-6 pt-4 border-t border-slate-200 flex flex-wrap gap-2">
                    <span className="text-sm text-slate-500 mr-2">Update Status:</span>
                    {['pending', 'contacted', 'approved', 'rejected'].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateRequestStatus(request.id, status)}
                        disabled={updating === request.id || request.status === status}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors disabled:opacity-50 ${
                          request.status === status
                            ? 'bg-slate-200 text-slate-500 cursor-default'
                            : status === 'approved'
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : status === 'rejected'
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : status === 'contacted'
                                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        {updating === request.id ? '...' : status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EarlyAccessRequests;
